<script setup lang="ts" generic="T extends Option">
import LoadingSpin from "@/components/icons/LoadingSpin.vue";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
  TransitionRoot,
} from "@headlessui/vue";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/vue/20/solid";
import { useDebounceFn } from "@vueuse/core";
import Fuse from "fuse.js";
import { isArray } from "lodash";
import { Ref, computed, ref, watch } from "vue";

export interface Option {
  id: number | string;
  name: string | undefined;
}
interface FuseOptions {
  includeScore?: boolean;
  threshold?: number;
  findAllMatches?: boolean;
  shouldSort?: boolean;
}

interface Props {
  labelOptions: string[];
  labelIcons: string[];
  options: Option[][];
  modelValue: T | any;
  limit?: number;
  enableFuse?: boolean;
  fuseOptions?: FuseOptions;
  fuseKeys?: string[];
  loadingOptions?: boolean;
  label?: string;
  placeholder?: string;
  defaultValue?: T;
  multiple?: boolean;
  isRequired?: boolean;
  disabled?: boolean;
  disabledClass?: string;
  debounceDelay?: number;
  resetQueryAfterSelect?: boolean;
  hideOptionsAfterSelect?: boolean;
  showInput?: boolean;
  showButton?: boolean;
  inputTooltip?: string;
  error?: string | Ref<string>;
  showOptionsByButton?: boolean;
  inputOverlapTooltip?: string;
  displayValue?: (value: T) => string;
  onModelChange?: (value: T | undefined) => void;
  afterModelChange?: (value: T | undefined) => void;
  onInputChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  beforeEnter?: () => void;
  afterEnter?: () => void;
  beforeLeave?: () => void;
  afterLeave?: () => void;
}
interface Emits {
  (event: "update:modelValue", value: any): boolean;
}

const props = withDefaults(defineProps<Props>(), {
  options: Array<any>,
  label: "",
  placeholder: "",
  enableFuse: false,
  resetQueryAfterSelect: true,
  hideOptionsAfterSelect: false,
  showInput: true,
  showButton: true,
  multiple: false,
  isRequired: false,
  onModelChange: undefined,
  showOptionsByButton: false,
  fuseKeys: () => [],
  displayValue: () => "",
  onInputChange: () => null,
  onFocus: () => null,
  onBlur: () => null,
  afterLeave: () => null,
  afterEnter: () => null,
  beforeLeave: () => null,
  beforeEnter: () => null,
});

const emit = defineEmits<Emits>();
const value = ref<T>();
const query = ref<string>("");
const inputElement = ref<any>({ el: null });
const compoboxElement = ref<any>({ el: null });
const showOptions = ref<boolean>(false);

const filteredOptions = computed(() => {
  if (!props.enableFuse) return props.options;

  const {
    includeScore = false,
    threshold = 0.2,
    findAllMatches = true,
    shouldSort = true,
  } = props.fuseOptions || {};

  const fuse = new Fuse(props.options, {
    ...props.fuseOptions,
    keys: props.fuseKeys,
    includeScore,
    threshold,
    findAllMatches,
    shouldSort,
  });

  return query.value.length
    ? fuse.search(query.value).map((result) => result.item)
    : props.options;
});

const inputChange = useDebounceFn(
  (
    $event: Event & {
      target: HTMLInputElement;
    },
  ) => {
    props.onInputChange($event.target.value);
    query.value = $event.target.value;
  },
  props.debounceDelay ?? 1,
);

const inputFocus = () => {
  props.onFocus();
};

const inputBlur = () => {
  if (query.value != "" && props.resetQueryAfterSelect && props.modelValue)
    setTimeout(() => (query.value = ""), 250);

  props.onBlur();
  showOptions.value = false;
};

const canAdd = computed(() => {
  if (isArray(props.modelValue) && props.multiple && props.limit) {
    return props.modelValue.length < (props.limit ?? 0);
  }
  return true;
});

const touchInput = () => {
  inputElement.value?.el.blur();
  setTimeout(() => {
    inputElement.value?.el.focus();
  }, 10);
};

watch(
  () => props.modelValue,
  (newModelValue) => {
    value.value = newModelValue;
  },
);

watch(value, (newValue, oldValue) => {
  if (!isArray(newValue) || (isArray(newValue) && canAdd)) {
    props.onModelChange
      ? props.onModelChange(newValue)
      : emit("update:modelValue", newValue);
  }

  if (!newValue && !oldValue) {
    return;
  }

  query.value = "";
  if (inputElement.value?.el) {
    inputElement.value.el.value = null;
  }

  if (props.hideOptionsAfterSelect || !props.showInput) {
    showOptions.value = false;
    if (inputElement.value?.el) {
      inputElement.value.el.blur();
    }
  } else {
    touchInput();
  }

  props.afterModelChange && props.afterModelChange(newValue);

  if (newValue == undefined) {
    setTimeout(() => {
      inputElement.value?.el?.focus();
    }, 10);
  }
});

watch(query, (value) => {
  if (value == "") {
    props.onInputChange("");
    showOptions.value = false;
  } else {
    showOptions.value = true;
  }
});

const getDisplayValue = (item: any) => {
  return item ? props.displayValue(item as T) : query.value;
};
</script>

<template>
  <div>
    <Combobox
      ref="compoboxElement"
      as="div"
      :disabled="disabled"
      v-model="value"
      :multiple="multiple"
      :default-value="defaultValue as T"
      :by="(a: T, z: T) => (a && z ? a.id == z.id : false)"
      :class="['flex flex-col gap-1']"
      v-slot="{ open }"
      @update:model-value="(value) => emit('update:modelValue', value)"
    >
      <ComboboxLabel
        v-if="label"
        class="text-base font-medium text-gray-900 flex gap-2 items-center"
      >
        <p>{{ label }} <span v-if="isRequired" class="text-red-500">*</span></p>

        <LoadingSpin v-if="loadingOptions" class="!h-4 !w-4" dark />
      </ComboboxLabel>

      <slot
        v-else
        name="label"
        :loading="loadingOptions"
        :required="isRequired"
      ></slot>

      <div class="relative">
        <div
          class="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm text-black"
        >
          <div :class="['w-full relative', error && !open && 'mb-[0.75rem]']">
            <ComboboxInput
              v-show="showInput"
              v-tooltip="inputTooltip"
              :model-value="query"
              ref="inputElement"
              :display-value="getDisplayValue"
              :placeholder="placeholder"
              :class="[
                'py-[0.625rem] px-[0.545rem]  bg-white rounded-md w-full border-2 text-sm text-black',
                disabled &&
                  (disabledClass ?? '!bg-gray-100 cursor-not-allowed'),
              ]"
              @focus="inputFocus"
              @blur="inputBlur"
              @change="inputChange"
            />

            <div v-if="!showInput">
              <figure
                class="w-full h-[44px] rounded-md ring-2 ring-inset bg-white py-[0.438rem] px-[1.625rem] capitalize text-gray-900 shadow-sm sm:text-sm sm:leading-6 flex gap-2 justify-between items-center ring-gray-300"
                v-tooltip="inputOverlapTooltip"
              >
                <slot name="input-overlap"></slot>
              </figure>
            </div>

            <ComboboxButton
              v-show="showInput && showButton"
              class="absolute inset-y-0 right-0 flex items-center pr-2"
              @click.prevent="showOptions = !showOptions"
            >
              <ChevronUpDownIcon
                class="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </ComboboxButton>
          </div>

          <span v-if="error && !open" class="!text-sm text-red-500 mt-6">{{
            error
          }}</span>
        </div>

        <ComboboxOptions
          v-if="canAdd && showInput"
          :static="showOptionsByButton"
          :class="[
            'absolute mt-1 z-10 max-h-96 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm',
          ]"
        >
          <div
            v-if="!query.length"
            class="relative cursor-default select-none p-2 text-gray-700"
          >
            Escriba para buscar...
          </div>

          <div
            v-else-if="filteredOptions.every(group => group.length === 0) && query.length > 0"
            class="relative cursor-default select-none p-2 text-gray-700"
          >
            <slot name="no-results" :query="query">
              Sin resultados.
            </slot>
          </div>

          <div
            v-else-if="loadingOptions"
            class="relative cursor-default select-none p-2 text-gray-700"
          >
            Cargando...
          </div>

          <template v-else>
            <template v-for="(label, i) in labelOptions" :key="i">
              <h3
                class="text-base border-b-2 pl-2 font-bold"
                v-if="filteredOptions[i]?.length > 0"
              >
                {{ label }}
              </h3>

              <ComboboxOption
                v-for="(option, index) in filteredOptions[i]"
                as="template"
                :key="index"
                :value="option"
                v-slot="{ selected, active }"
                @click="inputBlur"
              >
                <div v-if="filteredOptions[i]">
                  <div
                    :class="[
                      'flex items-starts p-2 text-gray-900',
                      active
                        ? 'bg-[#1F2937] text-white'
                        : 'bg-white text-gray-900',
                      selected && 'font-semibold',
                    ]"
                  >
                    <span
                      v-if="labelIcons.length > 0"
                      class="material-symbols-outlined mt-0.5 mr-2"
                      >{{ labelIcons[i] }}</span
                    >
                    <p class="text-sm capitalize">
                      {{ option.name }}
                    </p>

                    <CheckIcon
                      v-if="selected"
                      class="h-5 w-5 text-teal-500"
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </ComboboxOption>
            </template>
          </template>

          <div
            v-if="query.length > 0 && !loadingOptions && !filteredOptions.every(group => group.length === 0)"
            class="border-t border-gray-100"
          >
            <slot name="after-options" :query="query"></slot>
          </div>
        </ComboboxOptions>
      </div>
    </Combobox>
  </div>
</template>

<style scoped></style>
